import React, { useState, useRef, useEffect, useCallback } from "react";
import Modal from "react-modal";
import config from "../configs/config";
import { useAuth } from "../providers/AuthProvider";
import { useToast } from "../contexts/ToastContext";
import CommonButton from "../elments/CommonButton";
import SingleResponsePreview from "./Survey/SingleResponsePreview";

const SurveyPreview = ({
  sdataGroup: initialSdataGroup,
  surveyName,
  surveyFileName,
  oeParameters,
  onShowToastMessage,
  onSetProcessingMessage,
  questionnaireFileId,
  exportedFiles = {},
  projectId,
  selectedClient,
  selectedProject,
  onSurveyUpdate,
  powner,
  pid,
  pname
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState("questions");
  const [sdataGroup, setSdataGroup] = useState(initialSdataGroup);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowedDownloadFeatures, setAllowedDownloadFeatures] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Per-question comments, master comment & AI review states
  const [comments, setComments] = useState({});
  const [masterComment, setMasterComment] = useState("");
  const [isSubmittingToAI, setIsSubmittingToAI] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [aiChanges, setAiChanges] = useState([]);
  const [aiSubmitMessage, setAiSubmitMessage] = useState(null);
  const [aiSubmitAttempts, setAiSubmitAttempts] = useState(0);

  const recognitionRef = useRef(null);
  const containerRef = useRef(null);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= (sdataGroup ? Object.keys(sdataGroup).length - 1 : 0);

  const handleNext = () => !isLast && setCurrentIndex((prev) => prev + 1);
  const handleBack = () => !isFirst && setCurrentIndex((prev) => prev - 1);

  const currentQuestion = sdataGroup ? Object.values(sdataGroup)[currentIndex] : null;

  // Sync sdataGroup when prop changes & initialise speech recognition
  useEffect(() => {
    setSdataGroup(initialSdataGroup);
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.onstart = () => setTranscript("Listening...");
      recognitionRef.current.onresult = (event) => {
        if (isSpeaking) return;
        const command = event.results[0][0].transcript.toLowerCase().trim();
        if (command.length < 3) return;
        setTranscript(command);
        handleVoiceCommand(command);
      };
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error !== "aborted" && event.error !== "not-allowed") {
          setTranscript(`Error: ${event.error}`);
          speak("Try again.");
        }
        setIsListening(false);
      };
      recognitionRef.current.onend = () => {
        setIsListening(false);
        setTranscript("");
      };
    }
  }, [initialSdataGroup, isSpeaking]);

  // Load DOCX preview when split view is enabled
  useEffect(() => {
    if (showSplitView && !embedUrl && questionnaireFileId) {
      loadDocxPreview();
    }
  }, [showSplitView]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization) {
      if (
        organization.download_features &&
        Array.isArray(organization.download_features) &&
        organization.download_features.length > 0
      ) {
        setAllowedDownloadFeatures(organization.download_features);
      } else {
        setAllowedDownloadFeatures([]);
      }
    } else {
      setAllowedDownloadFeatures([]);
    }
  }, [user]);

  const hasDownloadFeatureAccess = (feature) =>
    allowedDownloadFeatures.includes(feature);

  const showToastMessage = (message, type = 'info') => {
    if (onShowToastMessage) {
      onShowToastMessage(message);
    } else {
      const fn = toast[type] ?? toast.info;
      fn(message);
    }
  };

  const openSurveyPreview = () => {
    const angularBaseUrl = process.env.REACT_APP_SURVEY_PREVIEW_URL || 'http://localhost:8100';
    if (!pid) {
      alert("PID not available. Please save the survey first.");
      return;
    }
    window.open(`${angularBaseUrl}/survey/${pid}`, '_blank');
  };

  const setProcessingMessage = (message) => {
    if (onSetProcessingMessage) onSetProcessingMessage(message);
  };

  // ─── Document preview ───────────────────────────────────────────────────────
  const loadDocxPreview = async () => {
    if (!questionnaireFileId) {
      showToastMessage("No document available for preview");
      return;
    }
    try {
      setIsLoadingDocx(true);
      setProcessingMessage("Loading document preview...");
      const response = await fetch(`${config.API_BASE_URL}/get_embed_url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          onedrive_file_id: questionnaireFileId,
          surveyFileName,
          powner,
        }),
      });
      if (!response.ok) throw new Error("Failed to get embed URL");
      const data = await response.json();
      if (data.success) {
        setEmbedUrl(data.embed_url);
      } else {
        showToastMessage("Failed to load document preview");
        setShowSplitView(false);
      }
    } catch (error) {
      console.error("Error previewing document:", error);
      showToastMessage("Error loading document preview");
      setShowSplitView(false);
    } finally {
      setIsLoadingDocx(false);
    }
  };

  // ─── AI review: submit questionnaire + comments ──────────────────────────────
  const handleSubmitToAI = async () => {
    if (aiSubmitAttempts >= 3) {
      setAiSubmitMessage({ success: false, text: "Maximum 3 attempts reached for this session. Please close and reopen the preview to reset." });
      return;
    }

    if (aiSubmitAttempts === 0) {
      const proceed = window.confirm(
        "Attempt 1 of 3\n\nHave you completed all your review comments? AI produces the best results when you send all your feedback in one comprehensive batch.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    } else if (aiSubmitAttempts === 1) {
      const proceed = window.confirm(
        "Attempt 2 of 3\n\nThis is your second submission. Make sure you have added all remaining comments before proceeding — you have only 1 attempt left after this.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    }

    try {
      setIsSubmittingToAI(true);
      setAiSubmitMessage(null);

      // Per-question items — only those with a comment
      const reviewItems = Object.entries(sdataGroup)
        .filter(([key]) => comments[key]?.trim())
        .map(([key, question]) => ({
          key,
          question,
          comment: comments[key].trim(),
        }));

      // Compact ordered survey list for master-comment context and _order calculation
      // ~20 tokens per question — NOT full JSON
      const allQuestions = Object.entries(sdataGroup)
        .map(([key, q]) => ({
          key,
          Question_number: q.Question_number || key,
          type: q.type || "",
          _order: q._order || 0,
          title_short: (q.Qtitle || "").replace(/<[^>]+>/g, "").substring(0, 60),
        }))
        .sort((a, b) => a._order - b._order);

      const response = await fetch(
        `${config.API_BASE_URL}/api/ai/review-with-comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            reviewItems,
            masterComment: masterComment.trim(),
            allQuestions,
            surveyFileName,
            questionnaireFileId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from AI");
      }

      const data = await response.json();
      const changes = [];

      // Map modified / deleted existing questions
      if (data.updatedQuestions) {
        Object.entries(data.updatedQuestions).forEach(([key, updated]) => {
          changes.push({
            questionKey: key,
            original: sdataGroup[key] || null,
            updated,
            isNew: false,
            status: "pending",
          });
        });
      }

      // Map new questions — each one is a separate accept/reject entry
      if (data.newQuestions?.length > 0) {
        data.newQuestions.forEach((nq, idx) => {
          const q = nq.question || {};
          const qKey = q.Question_number || `NEW_Q_${idx + 1}`;
          changes.push({
            questionKey: qKey,
            original: null,
            updated: q,
            isNew: true,
            insertAfterKey: nq.insertAfter || null,
            insertBeforeKey: nq.insertBefore || null,
            status: "pending",
          });
        });
      }

      if (changes.length > 0) {
        setAiChanges(changes);
        setShowReviewModal(true);
      } else {
        setAiSubmitMessage({
          success: false,
          text: data.message || "AI processed the request but no changes were proposed.",
        });
      }
      setAiSubmitAttempts((prev) => prev + 1);
    } catch (error) {
      console.error("AI submit error:", error);
      setAiSubmitMessage({ success: false, text: `Error: ${error.message}` });
      // Count failed attempts too so users can't loop around errors to bypass the limit
      setAiSubmitAttempts((prev) => prev + 1);
    } finally {
      setIsSubmittingToAI(false);
    }
  };

  // ─── AI review modal: accept / reject helpers ────────────────────────────────
  const acceptChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "accepted" } : c
      )
    );

  const rejectChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "rejected" } : c
      )
    );

  const acceptAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "accepted" })));

  const rejectAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "rejected" })));

  const applySelectedChanges = () => {
    const accepted = aiChanges.filter((c) => c.status === "accepted");
    if (accepted.length > 0) {
      const newSdataGroup = { ...sdataGroup };
      let deletedCount = 0;
      let modifiedCount = 0;
      let newCount = 0;

      accepted.forEach(({ questionKey, updated: upd, isNew }) => {
        if (upd?._deleted) {
          delete newSdataGroup[questionKey];
          deletedCount++;
        } else if (isNew) {
          // Insert new question — key might already exist if user resubmits,
          // so use Question_number from the object as the canonical key
          const finalKey = upd.Question_number || questionKey;
          newSdataGroup[finalKey] = upd;
          newCount++;
        } else {
          newSdataGroup[questionKey] = upd;
          modifiedCount++;
        }
      });

      setSdataGroup(newSdataGroup);
      if (onSurveyUpdate) onSurveyUpdate(newSdataGroup);
      setHasUnsavedChanges(true);

      const parts = [];
      if (modifiedCount > 0) parts.push(`${modifiedCount} modified`);
      if (deletedCount  > 0) parts.push(`${deletedCount} deleted`);
      if (newCount      > 0) parts.push(`${newCount} added`);

      setAiSubmitMessage({
        success: true,
        text: `Applied AI changes: ${parts.join(", ")} successfully.`,
      });
      setComments({});
      setMasterComment("");
    }
    setShowReviewModal(false);
    setAiChanges([]);
  };

  // ─── Save survey ─────────────────────────────────────────────────────────────
  const handleSaveSurvey = async () => {
    if (!projectId) {
      showToastMessage("No project ID available");
      return;
    }
    try {
      setIsSaving(true);
      setProcessingMessage("Saving survey...");
      const response = await fetch(`${config.API_BASE_URL}/save-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          sdataGroup,
          client: JSON.stringify(selectedClient),
          project: JSON.stringify(selectedProject),
        }),
      });
      if (!response.ok) throw new Error("Failed to save survey");
      await response.json();
      showToastMessage("Survey saved successfully!");
      setHasUnsavedChanges(false);
      if (onSurveyUpdate) onSurveyUpdate(sdataGroup);
      setProcessingMessage("");
    } catch (error) {
      console.error("Error saving survey:", error);
      showToastMessage("Failed to save survey");
    } finally {
      setIsSaving(false);
      setProcessingMessage("");
    }
  };

  const handlePreviewChange = (value) => {
    if (value === "psnst") {
      openSurveyPreview();   // open Angular preview in new tab
      return;               // do not change previewType
    }
    if (value.startsWith("export_")) {
      setSelectedExport(value.replace("export_", ""));
      setPreviewType("export");
    } else {
      setSelectedExport(null);
      setPreviewType(value);
    }
  };

  const toggleSplitView = () => {
    if (!questionnaireFileId) {
      showToastMessage("No document file available for this survey");
      return;
    }
    setShowSplitView(!showSplitView);
  };

  // ─── DOCX divider drag ───────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    },
    [isDragging]
  );

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // ─── Voice assistant ─────────────────────────────────────────────────────────
  const speak = (text) => {
    if ("speechSynthesis" in window && !isSpeaking) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.2;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    }
  };

  const handleVoiceCommand = (command) => {
    if (isSpeaking) return;
    let updated = false;
    const qMatch =
      command.match(/question (\d+(?:\.\d+)?)/i) ||
      command.match(/q(\d+(?:\.\d+)?)/i);
    const questionNum = qMatch ? parseInt(qMatch[1]) : null;
    const questionKey = questionNum
      ? questionNum.toString()
      : Object.keys(sdataGroup)[0];

    if (!sdataGroup || !sdataGroup[questionKey]) {
      speak("Question not found.");
      return;
    }

    const isBold = /bold|emphasize|strong/i.test(command);
    const isUnbold = /unbold|remove bold|normal/i.test(command);
    const targetTitle = /title|question text/i.test(command);
    const targetRow = command.match(/(?:row|option)s?\s*(\d+(?:\.\d+)?)/i);
    const targetCol = command.match(/(?:column|col)s?\s*(\d+(?:\.\d+)?)/i);
    const targetStmt = command.match(/(?:statement|item)s?\s*(\d+(?:\.\d+)?)/i);

    let target = null;
    let index = null;
    let subKey = null;

    if (targetTitle) {
      target = "Qtitle";
    } else if (targetRow) {
      const rowNum = parseInt(targetRow[1]) - 1;
      if (
        sdataGroup[questionKey]?.rows &&
        rowNum >= 0 &&
        rowNum < sdataGroup[questionKey].rows.length
      ) {
        target = "rows";
        index = rowNum;
        subKey = "text";
      } else {
        speak(`Row ${targetRow[1]} not found.`);
        return;
      }
    } else if (targetCol) {
      const colNum = parseInt(targetCol[1]) - 1;
      if (
        sdataGroup[questionKey]?.columns &&
        colNum >= 0 &&
        colNum < sdataGroup[questionKey].columns.length
      ) {
        target = "columns";
        index = colNum;
        subKey = "text";
      } else {
        speak(`Column ${targetCol[1]} not found.`);
        return;
      }
    } else if (targetStmt) {
      const stmtNum = parseInt(targetStmt[1]) - 1;
      if (
        sdataGroup[questionKey]?.statements &&
        stmtNum >= 0 &&
        stmtNum < sdataGroup[questionKey].statements.length
      ) {
        target = "statements";
        index = stmtNum;
        subKey = "text";
      } else {
        speak(`Statement ${targetStmt[1]} not found.`);
        return;
      }
    }

    if (target && (isBold || isUnbold)) {
      if (isBold) {
        updated = updateTextBold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`} bolded.`
        );
      } else if (isUnbold) {
        updated = updateTextUnbold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`
          } unbolded.`
        );
      }
    }

    if (!updated && command.length > 5 && !/stop|exit|cancel/i.test(command)) {
      setTimeout(() => {
        speak("Command not recognized. Try 'bold title' or 'unbold row two'.");
      }, 300);
    }

    if (updated) setSdataGroup((prev) => ({ ...prev }));
  };

  const updateTextBold = (questionKey, target, index = null, subKey = null) => {
    const toggleBold = (text) => {
      if (typeof text !== "string") return text;
      const boldRegex = /^<strong>\s*(.*?)\s*<\/strong>$/i;
      return boldRegex.test(text)
        ? text.replace(boldRegex, "$1").trim()
        : `<strong>${text}</strong>`;
    };
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = toggleBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = toggleBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const updateTextUnbold = (
    questionKey,
    target,
    index = null,
    subKey = null
  ) => {
    const stripBold = (text) =>
      typeof text !== "string"
        ? text
        : text.replace(/<strong>\s*(.*?)\s*<\/strong>/gi, "$1");
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = stripBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = stripBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      speak("Voice not supported.");
      return;
    }
    speechSynthesis.cancel();
    setIsSpeaking(false);
    if (isListening) {
      recognitionRef.current.stop();
      setTranscript("");
    } else {
      setTranscript("Ready...");
      recognitionRef.current.start();
    }
    setIsListening(!isListening);
    if (!isSpeaking) speak(isListening ? "Stopped." : "Say your command now.");
  };

  // ─── Row / column setting badges ──────────────────────────────────────────────
  const getRowSettingLabels = (row) => {
    const labels = [];
    if (row.screenout)
      labels.push({ text: "Screen Out", color: "#dc2626", bgColor: "#fecaca" });
    if (row.other)
      labels.push({ text: "Other", color: "#7c3aed", bgColor: "#ddd6fe" });
    if (row.otherPiping)
      labels.push({
        text: `Piping ${row.otherPiping}`,
        color: "#0369a1",
        bgColor: "#bae6fd",
      });
    if (row.filterLogic)
      labels.push({
        text: `Logic: ${row.filterLogic}`,
        color: "#ea580c",
        bgColor: "#fed7aa",
      });
    if (row.randomize)
      labels.push({
        text: "Randomize",
        color: "#059669",
        bgColor: "#a7f3d0",
      });
    if (row.exclusive)
      labels.push({
        text: "Exclusive",
        color: "#d97706",
        bgColor: "#fde68a",
      });
    if (row.hidden)
      labels.push({ text: "Hidden", color: "#4b5563", bgColor: "#d1d5db" });
    if (row.anchor)
      labels.push({ text: "Anchor", color: "#65a30d", bgColor: "#d9f99d" });
    if (row.rotationGroup)
      labels.push({
        text: `Rotation: ${row.rotationGroup}`,
        color: "#be185d",
        bgColor: "#fbcfe8",
      });
    if (row.fix)
      labels.push({ text: "Fix", color: "#4338ca", bgColor: "#c7d2fe" });
    if (row.header)
      labels.push({ text: "Header", color: "#0f766e", bgColor: "#99f6e4" });
    if (row.settings) {
      Object.entries(row.settings).forEach(([key, value]) => {
        if (
          value !== false &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          labels.push({
            text: `${key}: ${value}`,
            color: "#6b7280",
            bgColor: "#e5e7eb",
          });
        }
      });
    }
    return labels;
  };

  const badgeStyle = (bg, color) => ({
    fontSize: "0.75rem",
    padding: "3px 8px",
    backgroundColor: bg,
    color,
    borderRadius: "4px",
    fontWeight: 600,
    display: "inline-block",
  });

  const renderRowBadges = (row) => {
    if (!row) return null;
    const badges = [];
    if (row.screenout)
      badges.push(
        <span key="so" style={badgeStyle("#fee2e2", "#991b1b")}>
          Screen Out
        </span>
      );
    if (row.exclusive)
      badges.push(
        <span key="ex" style={badgeStyle("#dbeafe", "#1e40af")}>
          Exclusive
        </span>
      );
    if (row.other)
      badges.push(
        <span key="ot" style={badgeStyle("#fef3c7", "#92400e")}>
          Other (specify)
        </span>
      );
    if (row.fix)
      badges.push(
        <span key="fx" style={badgeStyle("#e0e7ff", "#3730a3")}>
          Fixed
        </span>
      );
    if (row.quotaCounts)
      badges.push(
        <span key="qt" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Quota: {row.quotaCounts}
        </span>
      );
    if (row.punchLogic)
      badges.push(
        <span key="pl" style={badgeStyle("#ecfdf5", "#065f46")}>
          Punch: {row.punchLogic}
        </span>
      );
    if (row.filterLogic)
      badges.push(
        <span key="fl" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Filter: {row.filterLogic}
        </span>
      );
    return badges.length > 0 ? (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginTop: "6px",
        }}
      >
        {badges}
      </div>
    ) : null;
  };

  // ─── Respondent (survey) view ─────────────────────────────────────────────────
  const renderSurveyView = (q) => {
    if (!q)
      return (
        <div style={{ color: "#6b7280", padding: "2rem" }}>
          No question data
        </div>
      );

    const titleHtml = { __html: q.Qtitle || "No question text" };
    const type = q.type || "Unknown";

    if (type === "Intro") {
      return (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
          <div
            style={{ fontSize: "1.25rem", marginBottom: "2rem" }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <button
            style={{
              padding: "12px 40px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "1rem",
            }}
          >
            CONTINUE
          </button>
        </div>
      );
    }

    if (type === "Single Response" && q.rows?.length > 0) {
      return <SingleResponsePreview question={q} />;
    }

    if (type === "Numeric") {
      const oe = q.oeparameters || {};
      return (
        <div>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              marginBottom: "1.6rem",
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <input
            type="number"
            min={oe.min}
            max={oe.max}
            placeholder="Enter your answer"
            readOnly
            style={{
              width: "200px",
              padding: "10px 14px",
              fontSize: "1rem",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
            }}
          />
        </div>
      );
    }

    if (q.rows?.length > 0 && q.columns?.length > 0) {
      const isMulti = type.includes("Multi") || type === "Multi Grid";
      return (
        <div>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              marginBottom: "1.6rem",
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "1000px",
              }}
            >
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      border: "1px solid #d1d5db",
                      minWidth: "400px",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    Statement / Item
                  </th>
                  {q.columns.map((col) => (
                    <th
                      key={col.index}
                      style={{
                        padding: "12px 14px",
                        textAlign: "center",
                        border: "1px solid #d1d5db",
                        minWidth: "130px",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        whiteSpace: "normal",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: col.text || "—" }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.rows.map((row, i) => (
                  <tr
                    key={row.index || i}
                    style={{
                      background: i % 2 === 0 ? "#ffffff" : "#f9fafb",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        fontSize: "0.98rem",
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          marginBottom: "6px",
                        }}
                        dangerouslySetInnerHTML={{ __html: row.text || "—" }}
                      />
                      {renderRowBadges(row)}
                    </td>
                    {q.columns.map((col) => (
                      <td
                        key={col.index}
                        style={{
                          textAlign: "center",
                          padding: "12px",
                          border: "1px solid #d1d5db",
                        }}
                      >
                        <input
                          type={isMulti ? "checkbox" : "radio"}
                          readOnly
                          style={{ width: "18px", height: "18px" }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: "#6b7280", padding: "2rem 0" }}>
        <div
          style={{ fontSize: "1.25rem" }}
          dangerouslySetInnerHTML={titleHtml}
        />
        <p style={{ marginTop: "1rem" }}>(No preview for type: {type})</p>
      </div>
    );
  };

  // ─── Export preview ───────────────────────────────────────────────────────────
  const renderExportPreview = () => {
    if (!selectedExport || !exportedFiles[selectedExport]) {
      return (
        <div
          style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}
        >
          <p>No export selected</p>
          <p style={{ fontSize: "14px", marginTop: "10px" }}>
            Select an export from the dropdown above to view its content
          </p>
        </div>
      );
    }
    const exportData = exportedFiles[selectedExport];
    return (
      <div className="export-preview-container">
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", color: "#374151" }}>
              {exportData.fileName}
            </h4>
            <p
              style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#6b7280" }}
            >
              Exported: {new Date(exportData.timestamp).toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => {
              const blob = new Blob([exportData.content], {
                type: "text/plain",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = exportData.fileName;
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Download
          </button>
        </div>
        <div
          style={{
            padding: "20px",
            overflowY: "auto",
            height: "calc(100% - 80px)",
            backgroundColor: "#ffffff",
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: "12px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              color: "#1f2937",
            }}
          >
            {exportData.content}
          </pre>
        </div>
      </div>
    );
  };

  // ─── JSON preview ─────────────────────────────────────────────────────────────
  const renderJSONPreview = () => (
    <div className="preview-json">
      <pre>{JSON.stringify(sdataGroup, null, 2)}</pre>
    </div>
  );

  // ─── Statistics preview ───────────────────────────────────────────────────────
  const renderStatisticsPreview = () => {
    if (!sdataGroup) return <div>No data available</div>;
    const totalQuestions = Object.keys(sdataGroup).length;
    const questionTypes = {};
    let totalRows = 0;
    let totalColumns = 0;
    let totalStatements = 0;
    let rowsWithSettings = 0;
    let columnsWithSettings = 0;
    Object.values(sdataGroup).forEach((question) => {
      questionTypes[question.type] = (questionTypes[question.type] || 0) + 1;
      totalRows += (question.rows || []).length;
      totalColumns += (question.columns || []).length;
      totalStatements += (question.statements || []).length;
      if (question.rows)
        question.rows.forEach((row) => {
          if (getRowSettingLabels(row).length > 0) rowsWithSettings++;
        });
      if (question.columns)
        question.columns.forEach((col) => {
          if (getRowSettingLabels(col).length > 0) columnsWithSettings++;
        });
    });
    return (
      <div className="preview-stats">
        <h4>Survey Statistics</h4>
        <div className="stats-grid">
          {[
            [totalQuestions, "Total Questions"],
            [totalRows, "Total Rows"],
            [totalColumns, "Total Columns"],
            [totalStatements, "Total Statements"],
            [rowsWithSettings, "Rows with Settings"],
            [columnsWithSettings, "Columns with Settings"],
          ].map(([val, label]) => (
            <div key={label} className="stat-item">
              <span className="stat-value">{val}</span>
              <span className="stat-label">{label}</span>
            </div>
          ))}
        </div>
        <div className="question-types">
          <h5>Question Types</h5>
          {Object.entries(questionTypes).map(([type, count]) => (
            <div key={type} className="type-item">
              <span className="type-name">{type}</span>
              <span className="type-count">{count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ─── Questions preview (with comment boxes) ───────────────────────────────────
  const renderQuestionsPreview = () => {
    if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
      return <div className="preview-empty">No questions available</div>;
    }

    const sortedEntries = Object.entries(sdataGroup).sort((a, b) => {
      return (a[1]._order || 0) - (b[1]._order || 0);
    });

    const hasAnyComment =
      Object.values(comments).some((c) => c?.trim()) || masterComment.trim().length > 0;

    return (
      <div>
        {sortedEntries.map(([questionNumber, question]) => (
          <div key={questionNumber} className="preview-question">
            <div className="preview-question-header">
              <span className="preview-qnumber">
                {question.Question_number || questionNumber}
              </span>
              <span className="preview-qtype">{question.type}</span>
            </div>

            <div
              className="preview-qtitle"
              dangerouslySetInnerHTML={{ __html: question.Qtitle || "No title" }}
            />

            {question.instructions && (
              <div className="preview-instructions">
                <strong>Instructions:</strong>
                <span
                  dangerouslySetInnerHTML={{ __html: question.instructions }}
                />
              </div>
            )}

            {question.logics && (
              <div className="preview-logic">
                <strong>Logic:</strong> {question.logics}
              </div>
            )}

            {question.rows && question.rows.length > 0 && (
              <div className="preview-rows">
                <strong>Rows ({question.rows.length}):</strong>
                <ul>
                  {question.rows.map((row, index) => {
                    const settingLabels = getRowSettingLabels(row);
                    return (
                      <li key={index} className="preview-row-item">
                        <div className="row-content">
                          <span className="row-index">
                            {row.index || index + 1}
                          </span>
                          <div className="row-text-and-settings">
                            <span
                              className="row-text"
                              dangerouslySetInnerHTML={{ __html: row.text }}
                            />
                            {settingLabels.length > 0 && (
                              <div className="row-settings-inline">
                                {settingLabels.map((label, li) => (
                                  <span
                                    key={li}
                                    className="row-setting-inline"
                                    style={{
                                      color: label.color,
                                      backgroundColor: label.bgColor,
                                      borderColor: label.color,
                                    }}
                                  >
                                    {label.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {question.columns && question.columns.length > 0 && (
              <div className="preview-columns">
                <strong>Columns ({question.columns.length}):</strong>
                <div className="preview-columns-list">
                  {question.columns.map((col, index) => {
                    const settingLabels = getRowSettingLabels(col);
                    return (
                      <div key={index} className="preview-column-item">
                        <span className="column-index">
                          {col.index || index + 1}.
                        </span>
                        <div className="column-text-and-settings">
                          <span
                            className="column-text"
                            dangerouslySetInnerHTML={{ __html: col.text }}
                          />
                          {settingLabels.length > 0 && (
                            <div className="column-settings-inline">
                              {settingLabels.map((label, li) => (
                                <span
                                  key={li}
                                  className="column-setting-inline"
                                  style={{
                                    color: label.color,
                                    backgroundColor: label.bgColor,
                                    borderColor: label.color,
                                  }}
                                >
                                  {label.text}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {question.statements && question.statements.length > 0 && (
              <div className="preview-statements">
                <strong>Statements ({question.statements.length}):</strong>
                <ul>
                  {question.statements.map((stmt, index) => (
                    <li
                      key={index}
                      dangerouslySetInnerHTML={{ __html: stmt.text }}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="preview-settings">
              {question.rowsRandomisation && (
                <span className="setting-badge">Rows Random</span>
              )}
              {question.columnsRandomisation && (
                <span className="setting-badge">Cols Random</span>
              )}
              {question.rowsRotation && (
                <span className="setting-badge">Rows Rotate</span>
              )}
              {question.columnsRotation && (
                <span className="setting-badge">Cols Rotate</span>
              )}
              {question.alphabeticalOrder && (
                <span className="setting-badge">A-Z Order</span>
              )}
              {question.columnIndex && (
                <span className="setting-badge">Col Index</span>
              )}
            </div>

            {question.oeparameters &&
              Object.entries(question.oeparameters).filter(
                ([, v]) =>
                  v !== false && v !== null && v !== undefined && v !== ""
              ).length > 0 && (
                <div className="preview-section">
                  <label>Parameters:</label>
                  <div className="preview-oe-params">
                    {Object.entries(question.oeparameters)
                      .filter(
                        ([, v]) =>
                          v !== false &&
                          v !== null &&
                          v !== undefined &&
                          v !== ""
                      )
                      .map(([key, value]) => (
                        <div key={key} className="oe-param-item">
                          <span className="param-key">{key}:</span>
                          <span className="param-value">{String(value)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* ── Per-question comment for AI ── */}
            <div
              style={{
                marginTop: "14px",
                paddingTop: "12px",
                borderTop: "1px dashed #e5e7eb",
              }}
            >
              <label
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#6b7280",
                  display: "block",
                  marginBottom: "5px",
                  letterSpacing: "0.3px",
                }}
              >
                Comment for AI:
              </label>
              <textarea
                value={comments[questionNumber] || ""}
                onChange={(e) =>
                  setComments((prev) => ({
                    ...prev,
                    [questionNumber]: e.target.value,
                  }))
                }
                placeholder="Describe what you'd like AI to change or review for this question..."
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: `1px solid ${comments[questionNumber]?.trim() ? "#7c3aed" : "#e5e7eb"
                    }`,
                  borderRadius: "6px",
                  fontSize: "13px",
                  resize: "vertical",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                  backgroundColor: comments[questionNumber]?.trim()
                    ? "#faf5ff"
                    : "white",
                  color: "#1f2937",
                  lineHeight: "1.5",
                  transition: "border-color 0.15s, background-color 0.15s",
                }}
              />
            </div>
          </div>
        ))}

        {/* ── Master comment box ── */}
        <div
          style={{
            margin: "24px 0 0",
            padding: "20px",
            border: "2px solid #7c3aed",
            borderRadius: "10px",
            backgroundColor: masterComment.trim() ? "#faf5ff" : "#fefefe",
            transition: "background-color 0.15s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span style={{ fontSize: "18px" }}>🧠</span>
            <label
              style={{
                fontSize: "13px",
                fontWeight: "700",
                color: "#6b21a8",
                letterSpacing: "0.3px",
              }}
            >
              Master Instruction for AI (applies to entire survey)
            </label>
          </div>
          <textarea
            value={masterComment}
            onChange={(e) => setMasterComment(e.target.value)}
            placeholder={
              "Examples:\n" +
              "• Add hidden quota questions that are missing for Q3 and Q5\n" +
              "• Add a screening termination question before Q1\n" +
              "• All grid questions should have columnsRandomisation: true\n" +
              "• Create a HIDDEN derived variable after Q8 that codes age groups"
            }
            rows={5}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${masterComment.trim() ? "#7c3aed" : "#d1d5db"}`,
              borderRadius: "7px",
              fontSize: "13px",
              resize: "vertical",
              fontFamily: "inherit",
              outline: "none",
              boxSizing: "border-box",
              backgroundColor: "transparent",
              color: "#1f2937",
              lineHeight: "1.55",
              transition: "border-color 0.15s",
            }}
          />
          <p style={{ margin: "6px 0 0", fontSize: "11px", color: "#9333ea" }}>
            This box sends ONE batch call to AI with a compact survey summary — token-efficient for survey-wide changes.
          </p>
        </div>

        {/* ── Submit to AI section ── */}
        <div
          style={{
            padding: "24px 20px",
            borderTop: "2px solid #e5e7eb",
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            backgroundColor: "#fafafa",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {aiSubmitMessage && (
            <div
              style={{
                width: "100%",
                maxWidth: "600px",
                padding: "10px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "500",
                backgroundColor: aiSubmitMessage.success ? "#d1fae5" : "#fee2e2",
                color: aiSubmitMessage.success ? "#065f46" : "#991b1b",
                border: `1px solid ${aiSubmitMessage.success ? "#6ee7b7" : "#fca5a5"}`,
                textAlign: "center",
              }}
            >
              {aiSubmitMessage.text}
            </div>
          )}

          <button
            onClick={handleSubmitToAI}
            disabled={isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3}
            title={
              aiSubmitAttempts >= 3
                ? "Maximum 3 attempts reached — close and reopen the preview to reset"
                : !hasAnyComment
                  ? "Add a per-question comment or a master instruction before submitting"
                  : `Submit all comments to AI (attempt ${aiSubmitAttempts + 1} of 3)`
            }
            style={{
              padding: "12px 40px",
              backgroundColor: isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3 ? "#d1d5db" : "#7c3aed",
              color: isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3 ? "#9ca3af" : "white",
              border: "none",
              borderRadius: "8px",
              cursor: isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3 ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              letterSpacing: "0.3px",
              transition: "background-color 0.2s",
            }}
          >
            {isSubmittingToAI
              ? "Submitting to AI…"
              : aiSubmitAttempts >= 3
                ? "Max Attempts Reached"
                : `Submit to AI (${aiSubmitAttempts + 1}/3)`}
          </button>

          {aiSubmitAttempts >= 3 ? (
            <p style={{ margin: 0, fontSize: "12px", color: "#dc2626" }}>
              Maximum 3 attempts reached. Close and reopen the preview to reset.
            </p>
          ) : !hasAnyComment ? (
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
              Add a per-question comment or fill the Master Instruction box to enable submission
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
              {3 - aiSubmitAttempts} attempt{3 - aiSubmitAttempts !== 1 ? "s" : ""} remaining — send all your comments in one batch for best results
            </p>
          )}
        </div>
      </div>
    );
  };

  // ─── AI Review Changes modal ──────────────────────────────────────────────────
  const renderReviewModal = () => {
    const pendingCount = aiChanges.filter((c) => c.status === "pending").length;
    const acceptedCount = aiChanges.filter((c) => c.status === "accepted").length;
    const rejectedCount = aiChanges.filter((c) => c.status === "rejected").length;

    return (
      <Modal
        isOpen={showReviewModal}
        onRequestClose={() => setShowReviewModal(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 20000,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            width: "92%",
            maxWidth: "1200px",
            height: "88vh",
            padding: "0",
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Modal header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", color: "#1f2937" }}>
              Review AI Changes
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#6b7280" }}>
              {aiChanges.length} question(s) updated by AI — review each change
              before applying
            </p>
          </div>

          {/* Bulk actions + summary */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontSize: "12px",
                color: "#6b7280",
                whiteSpace: "nowrap",
              }}
            >
              {acceptedCount} accepted · {rejectedCount} rejected ·{" "}
              {pendingCount} pending
            </span>
            <button
              onClick={acceptAllChanges}
              style={{
                padding: "7px 16px",
                backgroundColor: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              Accept All
            </button>
            <button
              onClick={rejectAllChanges}
              style={{
                padding: "7px 16px",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              Reject All
            </button>
            <button
              onClick={() => setShowReviewModal(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: "#6b7280",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable change list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {aiChanges.map((change) => {
            const isAccepted  = change.status === "accepted";
            const isRejected  = change.status === "rejected";
            const isDeletion  = change.updated?._deleted === true;
            const isNewQ      = change.isNew === true;

            const acceptLabel = isDeletion ? "🗑 Accept Deletion"
                              : isNewQ     ? "➕ Add Question"
                              :              "✓ Accept";
            const rejectLabel = isDeletion ? "↩ Keep Question"
                              : isNewQ     ? "✗ Skip"
                              :              "✗ Reject";

            // Header background by type
            const headerBg = isAccepted ? "#ecfdf5"
                           : isRejected ? "#fef2f2"
                           : isDeletion ? "#fff7ed"
                           : isNewQ     ? "#f0fdf4"
                           :              "#f9fafb";

            // Border colour by status/type
            const borderColor = isAccepted ? "#10b981"
                              : isRejected ? "#ef4444"
                              : isNewQ     ? "#059669"
                              :              "#e5e7eb";

            return (
              <div
                key={`${change.questionKey}_${change.isNew ? "new" : "mod"}`}
                style={{
                  marginBottom: "24px",
                  borderRadius: "10px",
                  border: `2px solid ${borderColor}`,
                  overflow: "hidden",
                  backgroundColor: "white",
                }}
              >
                {/* ── Change header ── */}
                <div
                  style={{
                    padding: "10px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: headerBg,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: "700", fontSize: "14px", color: "#1f2937" }}>
                      {change.questionKey}
                    </span>

                    {/* Short title (existing questions) */}
                    {!isNewQ && change.original?.Qtitle && (
                      <span
                        style={{ fontSize: "12px", color: "#6b7280" }}
                        dangerouslySetInnerHTML={{
                          __html: (change.original.Qtitle || "")
                            .replace(/<[^>]+>/g, "")
                            .substring(0, 80),
                        }}
                      />
                    )}

                    {/* Insertion position badge for new questions */}
                    {isNewQ && (change.insertAfterKey || change.insertBeforeKey) && (
                      <span style={{ fontSize: "11px", color: "#0369a1", backgroundColor: "#e0f2fe", padding: "2px 8px", borderRadius: "10px", fontWeight: "600" }}>
                        {change.insertAfterKey
                          ? `➕ Insert after ${change.insertAfterKey}`
                          : `➕ Insert before ${change.insertBeforeKey}`}
                      </span>
                    )}

                    {/* Status / action badges */}
                    {isNewQ && !isAccepted && !isRejected && (
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "#065f46", backgroundColor: "#d1fae5", padding: "2px 8px", borderRadius: "10px" }}>
                        ✨ New Question
                      </span>
                    )}
                    {isDeletion && !isAccepted && !isRejected && (
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "#b45309", backgroundColor: "#fef3c7", padding: "2px 8px", borderRadius: "10px" }}>
                        🗑 Deletion Proposed
                      </span>
                    )}
                    {isAccepted && (
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "#10b981", backgroundColor: "#d1fae5", padding: "2px 8px", borderRadius: "10px" }}>
                        ✓ {isDeletion ? "Deletion Accepted" : isNewQ ? "Will Be Added" : "Accepted"}
                      </span>
                    )}
                    {isRejected && (
                      <span style={{ fontSize: "11px", fontWeight: "700", color: "#ef4444", backgroundColor: "#fee2e2", padding: "2px 8px", borderRadius: "10px" }}>
                        ✗ {isDeletion ? "Kept" : isNewQ ? "Skipped" : "Rejected"}
                      </span>
                    )}
                  </div>

                  {/* Accept / reject buttons */}
                  <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                    <button
                      onClick={() => acceptChange(change.questionKey)}
                      style={{
                        padding: "5px 14px",
                        backgroundColor: isAccepted ? "#10b981" : (isNewQ ? "#f0fdf4" : (isDeletion ? "#fff7ed" : "#f0fdf4")),
                        color: isAccepted ? "white" : (isNewQ ? "#059669" : (isDeletion ? "#b45309" : "#10b981")),
                        border: `1px solid ${isAccepted ? "#10b981" : (isNewQ ? "#6ee7b7" : (isDeletion ? "#fcd34d" : "#6ee7b7"))}`,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {acceptLabel}
                    </button>
                    <button
                      onClick={() => rejectChange(change.questionKey)}
                      style={{
                        padding: "5px 14px",
                        backgroundColor: isRejected ? "#ef4444" : "#fff5f5",
                        color: isRejected ? "white" : "#ef4444",
                        border: `1px solid ${isRejected ? "#ef4444" : "#fca5a5"}`,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {rejectLabel}
                    </button>
                  </div>
                </div>

                {/* ── Body ── */}
                {isDeletion ? (
                  // Deletion: show original only
                  <div>
                    <div style={{ padding: "8px 14px", backgroundColor: "#fef2f2", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "700", color: "#dc2626" }}>
                      Question to be deleted — original content shown below
                    </div>
                    <div style={{ padding: "16px 20px", backgroundColor: "#fff8f8", display: "flex", alignItems: "flex-start", gap: "14px" }}>
                      <span style={{ fontSize: "28px", lineHeight: 1 }}>🗑️</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: "0 0 10px", fontSize: "13px", color: "#dc2626", fontWeight: "600" }}>
                          Accepting will permanently remove this question from the questionnaire.
                        </p>
                        <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "280px", overflowY: "auto", backgroundColor: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {JSON.stringify(change.original, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>

                ) : isNewQ ? (
                  // New question: full width preview
                  <div>
                    <div style={{ padding: "8px 14px", backgroundColor: "#f0fdf4", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "700", color: "#059669" }}>
                      ✨ Proposed new question — review JSON before accepting
                    </div>
                    <pre style={{ margin: 0, padding: "16px 20px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "380px", overflowY: "auto", backgroundColor: "#f8fff8", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {JSON.stringify(change.updated, null, 2)}
                    </pre>
                  </div>

                ) : (
                  // Modification: side-by-side diff
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0" }}>
                    <div style={{ borderRight: "1px solid #e5e7eb" }}>
                      <div style={{ padding: "8px 14px", backgroundColor: "#fef2f2", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "700", color: "#dc2626" }}>
                        Original
                      </div>
                      <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "320px", overflowY: "auto", backgroundColor: "#fff8f8", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {JSON.stringify(change.original, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div style={{ padding: "8px 14px", backgroundColor: "#f0fdf4", borderBottom: "1px solid #e5e7eb", fontSize: "12px", fontWeight: "700", color: "#059669" }}>
                        AI Proposed
                      </div>
                      <pre style={{ margin: 0, padding: "14px", fontSize: "11px", lineHeight: "1.55", overflowX: "auto", maxHeight: "320px", overflowY: "auto", backgroundColor: "#f8fff8", color: "#374151", fontFamily: 'Monaco, Consolas, "Courier New", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {JSON.stringify(change.updated, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Modal footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowReviewModal(false)}
            style={{
              padding: "9px 20px",
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: "7px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            Cancel
          </button>

          <button
            onClick={applySelectedChanges}
            disabled={acceptedCount === 0}
            style={{
              padding: "9px 28px",
              backgroundColor: acceptedCount === 0 ? "#d1d5db" : "#7c3aed",
              color: acceptedCount === 0 ? "#9ca3af" : "white",
              border: "none",
              borderRadius: "7px",
              cursor: acceptedCount === 0 ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: "600",
            }}
          >
            Apply {acceptedCount > 0 ? `${acceptedCount} ` : ""}
            Selected Change{acceptedCount !== 1 ? "s" : ""}
          </button>
        </div>
      </Modal>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <>
      <CommonButton
        label="Preview"
        title="Preview Survey"
        onClick={() => { setIsPreviewOpen(true); setAiSubmitAttempts(0); }}
      />

      {/* AI Review Changes modal */}
      {renderReviewModal()}

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            height: "95%",
            maxWidth: "1600px",
            borderRadius: "12px",
            padding: "0",
            overflow: "hidden",
          },
        }}
      >
        <div className="preview-modal">
          {/* Header */}
          <div className="preview-header">
            <h2>
              Survey Preview –{" "}
              {surveyName || surveyFileName || "Untitled Survey"}
            </h2>
            <div
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              {/* Preview type selector */}
              <div className="preview-tabs">
                <select
                  value={previewType === "export" ? `export_${selectedExport}` : previewType}
                  onChange={(e) => handlePreviewChange(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                    outline: "none",
                    minWidth: "200px",
                  }}
                >
                  <optgroup label="Survey Views">
                    <option value="questions">Questions</option>
                    <option value="statistics">Statistics</option>
                    <option value="json">Raw JSON</option>
                    <option value="survey">Survey</option>
                    <option value="psnst">PSNST Survey</option>   {/* new option */}
                  </optgroup>
                  {Object.keys(exportedFiles).length > 0 && (
                    <optgroup label="Exported Files">
                      {Object.entries(exportedFiles).map(([type, data]) => (
                        <option key={type} value={`export_${type}`}>
                          {type.toUpperCase()} ({data.extension}) –{" "}
                          {new Date(data.timestamp).toLocaleDateString()}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Document split view */}
              {questionnaireFileId && (
                <button
                  onClick={toggleSplitView}
                  disabled={isLoadingDocx}
                  style={{
                    backgroundColor: showSplitView ? "#10b981" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor: isLoadingDocx ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {isLoadingDocx
                    ? "Loading..."
                    : showSplitView
                      ? "✓ Split View"
                      : "📄 Show Doc"}
                </button>
              )}

              {/* Save button */}
              <button
                onClick={handleSaveSurvey}
                disabled={isSaving || !hasUnsavedChanges}
                style={{
                  backgroundColor: hasUnsavedChanges ? "#f59e0b" : "#9ca3af",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    isSaving || !hasUnsavedChanges ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: isSaving || !hasUnsavedChanges ? 0.6 : 1,
                }}
              >
                {isSaving
                  ? "⏳ Saving..."
                  : hasUnsavedChanges
                    ? "Save Changes"
                    : "✓ Saved"}
              </button>
              {/* Close */}
              <button
                onClick={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
                className="close-button"
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                }}
              >
                ×
              </button>

              {/* Voice */}
              <button
                onClick={toggleListening}
                className={`voice-button ${isListening ? "active" : ""}`}
                title="Voice Commands (e.g., 'bold title in question 1')"
                style={{
                  backgroundColor: isListening ? "#ef4444" : "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                {isListening ? "🔴" : "🎤"}
              </button>
              {transcript && (
                <div style={{ fontSize: "12px", color: "gray" }}>
                  Last: {transcript}
                </div>
              )}
            </div>
          </div>

          {/* Main content */}
          <div
            ref={containerRef}
            className="preview-content-wrapper"
            style={{
              display: "flex",
              height: "calc(100% - 70px)",
              position: "relative",
            }}
          >
            {/* Survey preview panel */}
            <div
              className="preview-content"
              style={{
                width: showSplitView ? `${splitRatio}%` : "100%",
                overflowY: "auto",
                padding: "20px",
                transition: isDragging ? "none" : "width 0.3s ease",
              }}
            >
              {previewType === "questions" && renderQuestionsPreview()}
              {previewType === "statistics" && renderStatisticsPreview()}
              {previewType === "json" && renderJSONPreview()}
              {previewType === "export" && renderExportPreview()}
              {previewType === "survey" && (
                <>
                  {renderSurveyView(currentQuestion)}
                  <div
                    style={{
                      padding: "12px 24px",
                      borderTop: "1px solid #e2e8f0",
                      background: "#f8fafc",
                      display: "flex",
                      justifyContent: "space-between",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={handleBack}
                      disabled={isFirst}
                      style={{
                        padding: "10px 28px",
                        background: isFirst ? "#e2e8f0" : "#64748b",
                        color: isFirst ? "#94a3b8" : "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: isFirst ? "not-allowed" : "pointer",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={isLast}
                      style={{
                        padding: "10px 36px",
                        background: isLast ? "#10b981" : "#2563eb",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: isLast ? "default" : "pointer",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      }}
                    >
                      {isLast ? "End of Survey" : "Next →"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* DOCX divider */}
            {showSplitView && (
              <div
                onMouseDown={handleMouseDown}
                style={{
                  width: "5px",
                  cursor: "col-resize",
                  backgroundColor: "#e5e7eb",
                  position: "relative",
                  flexShrink: 0,
                  transition: isDragging ? "none" : "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  if (!isDragging)
                    e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "20px",
                    height: "40px",
                    backgroundColor: isDragging ? "#3b82f6" : "#9ca3af",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "10px",
                  }}
                >
                  ⋮
                </div>
              </div>
            )}

            {/* DOCX preview panel */}
            {showSplitView && (
              <div
                className="docx-preview-panel"
                style={{
                  width: `${100 - splitRatio}%`,
                  backgroundColor: "#f9fafb",
                  display: "flex",
                  flexDirection: "column",
                  transition: isDragging ? "none" : "width 0.3s ease",
                  position: "relative",
                }}
              >
                {isDragging && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: "col-resize",
                    }}
                  />
                )}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Document Preview"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "#6b7280",
                      }}
                    >
                      {isLoadingDocx
                        ? "Loading document..."
                        : "No preview available"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .preview-modal {
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .preview-header h2 {
          margin: 0;
          color: #333;
          font-size: 18px;
        }

        .preview-tabs {
          display: flex;
          gap: 4px;
        }

        .preview-tabs button {
          padding: 8px 16px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          color: #6b7280;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .preview-tabs button:hover {
          color: #3b82f6;
        }

        .preview-tabs button.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        .preview-row-item {
          margin-bottom: 4px;
          padding: 4px 6px;
        }

        .row-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .row-index {
          font-weight: bold;
          min-width: 20px;
          color: #6b7280;
          font-size: 13px;
        }

        .row-text-and-settings {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .row-text {
          font-size: 13px;
        }

        .row-settings-inline {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .row-setting-inline {
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 8px;
          border: 1px solid;
          font-weight: 500;
          white-space: nowrap;
        }

        .preview-column-item {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 2px;
          padding: 2px 4px;
        }

        .column-index {
          font-weight: bold;
          min-width: 16px;
          color: #6b7280;
          font-size: 11px;
        }

        .column-text-and-settings {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }

        .column-text {
          font-size: 11px;
        }

        .column-settings-inline {
          display: flex;
          gap: 2px;
          flex-wrap: wrap;
        }

        .column-setting-inline {
          font-size: 9px;
          padding: 1px 3px;
          border-radius: 6px;
          border: 1px solid;
          font-weight: 500;
          white-space: nowrap;
        }

        .preview-rows ul {
          list-style: none;
          padding: 0;
          margin: 4px 0;
        }

        .preview-rows li {
          border-left: 2px solid #e5e7eb;
          padding-left: 8px;
        }

        .preview-columns-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .export-preview-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background-color: #ffffff;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
      `}</style>
    </>
  );
};

export default SurveyPreview;
